import { type BuiltInUniversalFilter } from '../../store.types';
import { type BackendSavedFilter, UNIVERSAL_FILTER_ALL_ID } from '../../types';

export type FilterBadge =
  | {
      baseline: readonly [];
      id: BuiltInUniversalFilter['id'];
      label: string;
      savedFilter: undefined;
    }
  | {
      baseline: BackendSavedFilter['filters'];
      id: BackendSavedFilter['id'];
      label: string;
      savedFilter: BackendSavedFilter;
    };

export type FilterBadgeState = {
  readonly countLabel: string | null;
  readonly isPersistedSavedFilter: boolean;
  readonly isSelected: boolean;
  readonly showDirty: boolean;
  readonly showEditPencil: boolean;
};

export function buildFilterBadges({
  allLabel,
  builtInFilters,
  savedFilters,
}: {
  readonly allLabel: string;
  readonly builtInFilters: readonly BuiltInUniversalFilter[];
  readonly savedFilters: readonly BackendSavedFilter[];
}): FilterBadge[] {
  return [
    ...builtInFilters.map((filter) => ({
      baseline: [] as const,
      id: filter.id,
      label: filter.id === UNIVERSAL_FILTER_ALL_ID ? allLabel : filter.label,
      savedFilter: undefined,
    })),
    ...savedFilters.map((filter) => ({
      baseline: filter.filters,
      id: filter.id,
      label: filter.name,
      savedFilter: filter,
    })),
  ];
}

export function getFilterBadgeState({
  badge,
  isDirty,
  resultCountsByFilterId,
  savedFilterIds,
  selectedFilterId,
}: {
  readonly badge: FilterBadge;
  readonly isDirty: boolean;
  readonly resultCountsByFilterId: Readonly<Record<string, number>>;
  readonly savedFilterIds: ReadonlySet<string>;
  readonly selectedFilterId: string;
}): FilterBadgeState {
  const isSelected = selectedFilterId === badge.id;
  const isPersistedSavedFilter = savedFilterIds.has(badge.id);

  return {
    countLabel: getFilterResultCountLabel(resultCountsByFilterId[badge.id]),
    isPersistedSavedFilter,
    isSelected,
    showDirty: isSelected && isDirty,
    showEditPencil:
      isSelected &&
      isPersistedSavedFilter &&
      badge.id !== UNIVERSAL_FILTER_ALL_ID,
  };
}

function getFilterResultCountLabel(count: number | undefined): string | null {
  if (typeof count !== 'number') {
    return null;
  }

  return count > 99 ? '99+' : String(count);
}

export type BadgeOverflowPartition = {
  readonly hiddenBadges: FilterBadge[];
  readonly visibleBadges: FilterBadge[];
};

/**
 * Partitions filter badges into the prefix that fits into the row as visible
 * tabs and the remainder that goes into the searchable overflow menu.
 *
 * Degrades gracefully: while the container or any badge has not been measured
 * yet (width 0/undefined — e.g. the first render, or jsdom in unit tests),
 * every badge stays visible and no overflow menu is shown, matching the
 * pre-overflow-menu behavior of the row.
 *
 * When the selected badge would be hidden, it is re-inserted as the LAST
 * visible badge (immediately before the overflow menu button) and the badges
 * it displaces move into the overflow menu instead.
 */
export function computeBadgeOverflow({
  addControlWidth,
  badges,
  containerWidth,
  gap,
  menuButtonWidth,
  selectedFilterId,
  widthById,
}: {
  readonly addControlWidth: number;
  readonly badges: readonly FilterBadge[];
  readonly containerWidth: number;
  readonly gap: number;
  readonly menuButtonWidth: number;
  readonly selectedFilterId: string;
  readonly widthById: ReadonlyMap<string, number>;
}): BadgeOverflowPartition {
  const isUnmeasured =
    containerWidth <= 0 || badges.some((badge) => !widthById.get(badge.id));

  if (isUnmeasured) {
    return { hiddenBadges: [], visibleBadges: [...badges] };
  }

  const widthOf = (badge: FilterBadge) => widthById.get(badge.id) ?? 0;

  const totalBadgesWidth = badges.reduce(
    (accumulator, badge, index) =>
      accumulator + widthOf(badge) + (index > 0 ? gap : 0),
    0,
  );

  // Everything (plus the add button) fits without an overflow menu button.
  if (totalBadgesWidth + gap + addControlWidth <= containerWidth) {
    return { hiddenBadges: [], visibleBadges: [...badges] };
  }

  // Reserve room for both the add button and the overflow menu button.
  const budget = containerWidth - addControlWidth - menuButtonWidth - 2 * gap;

  function fitPrefix(candidates: readonly FilterBadge[], maxWidth: number) {
    const prefix: FilterBadge[] = [];
    let usedWidth = 0;

    for (const badge of candidates) {
      const nextWidth =
        usedWidth + widthOf(badge) + (prefix.length > 0 ? gap : 0);
      if (nextWidth > maxWidth) break;
      usedWidth = nextWidth;
      prefix.push(badge);
    }

    return prefix;
  }

  const visibleBadges = fitPrefix(badges, budget);
  const hiddenBadges = badges.slice(visibleBadges.length);

  const selectedHiddenBadge = hiddenBadges.find(
    (badge) => badge.id === selectedFilterId,
  );

  if (!selectedHiddenBadge) {
    return { hiddenBadges, visibleBadges };
  }

  // The selected badge must stay visible as the LAST tab before the menu
  // button; refit the remaining badges around it.
  const otherBadges = badges.filter((badge) => badge.id !== selectedFilterId);
  const prefix = fitPrefix(
    otherBadges,
    budget - widthOf(selectedHiddenBadge) - gap,
  );

  return {
    hiddenBadges: otherBadges.slice(prefix.length),
    visibleBadges: [...prefix, selectedHiddenBadge],
  };
}
